"use client";

import { MantineProvider, AppShell } from '@mantine/core';
import { ReactNode, useEffect, useState, useRef } from 'react';
import { AuthGuard } from '@/components/auth/auth_guard';
import { useDisclosure } from '@mantine/hooks';
import { usePathname } from 'next/navigation';
import { NavbarSimple } from '@/components/navigation/navbar';
import TaskBar from '@/components/navigation/taskbar';
import { Header } from '@/components/header/header';
import OfflineBanner from '@/components/offline_banner/offline_banner';

interface ProvidersProps {
  children: ReactNode;
}

// Move excludedRoutes outside the component
const EXCLUDED_ROUTES = ['/auth', '/auth/reset-password', '/install'];

export default function Providers({ children }: ProvidersProps) {
  const [opened] = useDisclosure(false);
  const pathname = usePathname();
  const [shouldUseAppShell, setShouldUseAppShell] = useState(true);
  const [isMobile, setIsMobile] = useState(false);
  const [taskbarHeight, setTaskbarHeight] = useState(0);
  const taskbarRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    setShouldUseAppShell(!EXCLUDED_ROUTES.includes(pathname));
  }, [pathname]);

  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth <= 768); // Adjust the width as needed for your mobile breakpoint
    };

    handleResize(); // Set initial state
    window.addEventListener('resize', handleResize);

    return () => window.removeEventListener('resize', handleResize);
  }, []);

  useEffect(() => {
    // Prevent overscroll on the document body
    document.body.style.overscrollBehavior = 'none';
    document.documentElement.style.overscrollBehavior = 'none';
    
    return () => {
      document.body.style.overscrollBehavior = '';
      document.documentElement.style.overscrollBehavior = '';
    };
  }, []);

  useEffect(() => {
    const updateTaskbarHeight = () => {
      if (taskbarRef.current) {
        const computedStyle = window.getComputedStyle(taskbarRef.current);
        const isVisible = computedStyle.display !== 'none';
        
        if (isVisible) {
          const height = taskbarRef.current.offsetHeight;
          setTaskbarHeight(height);
        } else {
          setTaskbarHeight(0);
        }
      }
    };

    // Wait longer for initial render and styles to be applied
    const timeoutId = setTimeout(updateTaskbarHeight, 100);

    const resizeObserver = new ResizeObserver(() => {
      updateTaskbarHeight();
    });

    if (taskbarRef.current) {
      resizeObserver.observe(taskbarRef.current);
    }

    return () => {
      clearTimeout(timeoutId);
      resizeObserver.disconnect();
    };
  }, []);

  return (
    <MantineProvider
      theme={{
        components: {
          Input: {
            styles: {
              input: {
                backgroundColor: 'light-dark(var(--mantine-color-gray-0), var(--mantine-color-dark-9))',
                borderWidth: '1px',
                borderStyle: 'solid',
                borderColor: 'var(--input-border-color)',
                '&:focus, &:focusWithin': {
                  borderColor: 'var(--mantine-primary-color-filled)'
                }
              }
            }
          },
          Select: {
            styles: {
              input: {
                backgroundColor: 'light-dark(var(--mantine-color-gray-0), var(--mantine-color-dark-9))',
                borderWidth: '1px',
                borderStyle: 'solid',
                borderColor: 'var(--input-border-color)',
                '&:focus, &:focusWithin': {
                  borderColor: 'var(--mantine-primary-color-filled)'
                }
              },
              dropdown: {
                backgroundColor: 'light-dark(var(--mantine-color-gray-0), var(--mantine-color-dark-9))',
                borderWidth: '1px',
                borderStyle: 'solid',
                borderColor: 'var(--input-border-color)'
              }
            }
          },
          DateInput: {
            styles: {
              input: {
                backgroundColor: 'light-dark(var(--mantine-color-gray-0), var(--mantine-color-dark-9))',
                borderWidth: '1px',
                borderStyle: 'solid',
                borderColor: 'var(--input-border-color)',
                '&:focus, &:focusWithin': {
                  borderColor: 'var(--mantine-primary-color-filled)'
                }
              }
            }
          },
          AppShell: {
            styles: {
              main: {
                backgroundColor: 'light-dark(var(--mantine-color-white), var(--mantine-color-dark-9))',
                overscrollBehavior: 'none'
              }
            }
          }
        }
      }}
    >
      <AuthGuard excludeRoutes={EXCLUDED_ROUTES}>
        {shouldUseAppShell ? (
          isMobile ? (
            <AppShell 
              padding="sm"
              styles={{
                main: {
                  backgroundColor: 'light-dark(var(--mantine-color-white), var(--mantine-color-dark-9))',
                  overscrollBehavior: 'none'
                }
              }}
            >
              <div 
                style={{ 
                  height: 'calc(env(safe-area-inset-top))',
                  position: 'fixed',
                  top: 0,
                  left: 0,
                  right: 0,
                  backgroundColor: 'light-dark(var(--mantine-color-white), var(--mantine-color-dark-9))',
                  zIndex: 1000
                }} 
              />
              <div style={{ 
                marginTop: 'calc(env(safe-area-inset-top))', 
                marginBottom: `calc(${taskbarHeight}px + env(safe-area-inset-bottom, 0px))`,
                overscrollBehavior: 'none'
              }}>
                <AppShell.Main style={{ 
                  backgroundColor: 'light-dark(var(--mantine-color-white), var(--mantine-color-dark-9))',
                  overscrollBehavior: 'none',
                  minHeight: `calc(100vh - env(safe-area-inset-top) - env(safe-area-inset-bottom) - ${taskbarHeight}px)`,
                }}>
                  {children}
                </AppShell.Main>
              </div>
              <TaskBar ref={taskbarRef} />
            </AppShell>
          ) : (
            <AppShell
              header={{ height: 60 }}
              navbar={{
                width: 200,
                breakpoint: 'sm',
                collapsed: { mobile: !opened },
              }}
              padding="md"
              styles={{
                main: {
                  backgroundColor: 'light-dark(var(--mantine-color-white), var(--mantine-color-dark-9))',
                  overscrollBehavior: 'none'
                },
                navbar: {
                  backgroundColor: 'light-dark(var(--mantine-color-white), var(--mantine-color-dark-9))'
                },
                header: {
                  backgroundColor: 'light-dark(var(--mantine-color-white), var(--mantine-color-dark-9))'
                }
              }}
            >
              <AppShell.Header>
                <Header />
              </AppShell.Header>

              <AppShell.Navbar p="0" style={{ backgroundColor: 'light-dark(var(--mantine-color-white), var(--mantine-color-dark-9))' }}>
                <NavbarSimple />
              </AppShell.Navbar>

              <AppShell.Main style={{ 
                backgroundColor: 'light-dark(var(--mantine-color-white), var(--mantine-color-dark-9))',
                overscrollBehavior: 'none',
                minHeight: '100vh',
              }}>
                <OfflineBanner />
                {children}
              </AppShell.Main>
              <TaskBar ref={taskbarRef} />
            </AppShell>
          )
        ) : (
          // Separate layout for auth pages or excluded routes
          <div style={{ 
            padding: '2rem', 
            display: 'flex', 
            flexDirection: 'column', 
            alignItems: 'center', 
            justifyContent: 'center', 
            minHeight: '100vh', 
            width: '100vw', 
            backgroundColor: 'light-dark(var(--mantine-color-white), var(--mantine-color-dark-9))',
            overscrollBehavior: 'none'
          }}>
            {children}
          </div>
        )}
      </AuthGuard>
    </MantineProvider>
  );
}
